import moment, { type Moment } from 'moment';
import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { translateString } from '@src/util/intl/translateString';

export const HOLIDAYS_LIST_FILTER_FIELDS = {
  NAME: 'name',
  DATE: 'date',
  COUNTRY: 'country',
  ACTIVE: 'active',
} as const;

export interface HolidaysListFilterState {
  name: string;
  date: Moment | null;
  country: string;
  active: boolean | null;
}

export const INIT_HOLIDAYS_LIST_FILTERS: FilterValues = {
  [HOLIDAYS_LIST_FILTER_FIELDS.NAME]: {
    value: '',
    label: translateString('holiday.filters.name'),
    valueType: 'string',
  },
  [HOLIDAYS_LIST_FILTER_FIELDS.DATE]: {
    value: null,
    label: translateString('holiday.filters.date'),
    valueType: 'null',
    clearValue: null,
    hideWhenCleared: true,
  },
  [HOLIDAYS_LIST_FILTER_FIELDS.COUNTRY]: {
    value: '',
    label: translateString('holiday.filters.country'),
    valueType: 'string',
  },
  [HOLIDAYS_LIST_FILTER_FIELDS.ACTIVE]: {
    value: true,
    label: translateString('holiday.filters.active'),
    valueTranslated: translateString('general.yes'),
    valueType: 'boolean',
    clearValue: null,
    hideWhenCleared: true,
  },
};

export const formatHolidayDateForTag = (dateValue: unknown): string => {
  if (!dateValue) return '';
  const date = moment(dateValue as string);
  if (!date.isValid()) return '';
  return date.format('L');
};

export const filterValuesToHolidaysListFilters = (
  values: FilterValues,
): HolidaysListFilterState => {
  const name = String(values[HOLIDAYS_LIST_FILTER_FIELDS.NAME]?.value ?? '').trim();
  const country = String(values[HOLIDAYS_LIST_FILTER_FIELDS.COUNTRY]?.value ?? '');
  const rawActive = values[HOLIDAYS_LIST_FILTER_FIELDS.ACTIVE]?.value;
  const active = typeof rawActive === 'boolean' ? rawActive : null;

  const rawDate = values[HOLIDAYS_LIST_FILTER_FIELDS.DATE]?.value;
  let date: Moment | null = null;
  if (rawDate) {
    const parsed = moment(rawDate as string);
    if (parsed.isValid()) date = parsed;
  }

  return { name, date, country, active };
};

export const buildHolidaysListFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
  valueTranslated?: string,
): SetValueProps => {
  switch (key) {
    case HOLIDAYS_LIST_FILTER_FIELDS.NAME:
      return {
        key,
        value: String(value ?? ''),
        label: intl.formatMessage({ id: 'holiday.filters.name' }),
        valueTranslated: String(value ?? ''),
        valueType: 'string',
      };
    case HOLIDAYS_LIST_FILTER_FIELDS.DATE: {
      const serialized = value ? moment(value as string | Moment).toISOString() : null;
      return {
        key,
        value: serialized,
        label: intl.formatMessage({ id: 'holiday.filters.date' }),
        valueTranslated: formatHolidayDateForTag(serialized),
        valueType: 'null',
        clearValue: null,
        hideWhenCleared: true,
      };
    }
    case HOLIDAYS_LIST_FILTER_FIELDS.COUNTRY:
      return {
        key,
        value: String(value ?? ''),
        label: intl.formatMessage({ id: 'holiday.filters.country' }),
        valueTranslated: valueTranslated ?? String(value ?? ''),
        valueType: 'string',
      };
    case HOLIDAYS_LIST_FILTER_FIELDS.ACTIVE: {
      const checked = typeof value === 'boolean' ? value : null;
      return {
        key,
        value: checked,
        label: intl.formatMessage({ id: 'holiday.filters.active' }),
        valueTranslated:
          valueTranslated ??
          (checked === null
            ? ''
            : intl.formatMessage({ id: checked ? 'general.yes' : 'general.no' })),
        valueType: 'boolean',
        clearValue: null,
        hideWhenCleared: true,
      };
    }
    default:
      return { key, value: value as SetValueProps['value'], valueType: 'string' };
  }
};
